import { useLoaderData } from '@remix-run/react';
import { GQLJournalCarouselFragment } from '@theprintbar/graphql';
import { BrandColours, WebRoutes, cn } from '@theprintbar/shared';
import {
	ButtonSize,
	Carousel,
	CarouselNextButton,
	CarouselPreviousButton,
	RoundedCTA,
	RoundedCTASkeleton,
	SmallArrowIcon,
	TitleSection,
} from '@theprintbar/ui';
import { loader } from '~/routes/_layout/$/_index';

interface JournalCarouselProps {
	content: GQLJournalCarouselFragment;
}

export function JournalCarousel({ content, ...titleSectionProps }: JournalCarouselProps) {
	const { journalArticles } = useLoaderData<typeof loader>();

	return (
		<TitleSection
			content={content.journalCarouselContainer}
			className={cn('bg-blue-mid border-y border-blue py-16 my-0 lg:my-0')}
			buttonIcon={SmallArrowIcon}
			buttonSize={ButtonSize.S}
			isButtonStacked
			fullWidthChild
			{...titleSectionProps}
		>
			{journalArticles ? (
				<>
					<Carousel
						carouselKey="Journal"
						slides={journalArticles.map(({ slug, heroBanner, id, category }, index) => ({
							containerProps: {
								className: 'relative pb-8 lg:pb-16',
							},
							content: (
								<RoundedCTA
									gravityTitle
									cta={{
										structure: {
											...heroBanner.structure?.[0],
											links: [
												{
													to: WebRoutes.JournalPage(slug),
													text: '',
												},
											],
										},
									}}
									containerClassName="px-4 lg:px-8"
									ctaClassName="bg-white-pearl aspect-[5/3] max-h-[32rem]"
									imageContainerClassName="border-none lg:border-none aspect-[5/3] max-h-[32rem] lg:aspect-auto"
									pill={
										category.colour && category.title
											? {
													text: category.title,
													colour:
														category.colour === BrandColours.MidBlue
															? BrandColours.BrightBlue
															: category.colour,
											  }
											: undefined
									}
									split
									even
									reversed
								/>
							),
						}))}
					>
						<div className="flex space-x-8 ml-3 lg:ml-8">
							<CarouselPreviousButton carouselKey="Journal" />
							<CarouselNextButton carouselKey="Journal" />
						</div>
					</Carousel>
				</>
			) : (
				<div>
					{new Array(1).fill('').map((_, index) => (
						<div key={index} className="relative pb-8 lg:pb-16 px-4 lg:px-8">
							<RoundedCTASkeleton split />
						</div>
					))}
					<div className="flex space-x-8 ml-3 lg:ml-8">
						<CarouselPreviousButton disabled carouselKey="Journal" />
						<CarouselNextButton disabled carouselKey="Journal" />
					</div>
				</div>
			)}
		</TitleSection>
	);
}
